import { ReactElement, useMemo } from "react";
import styled from "@emotion/styled";
import { Area, Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from "recharts";
import { ColorProp } from "styles/themes";

import { numberFormatLong } from "components/app_layout/Typography/Numbers";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "components/ui/chart/Chart";
import { useLegendToggle } from "components/ui/chart/useLegendToggle";
import EmptyState from "components/ui/empty_state/EmptyState";
import { formatShortAmount } from "lib/utils";
import { SpendChartType, SpendMeasure } from "./hooks/useSpendUrlState";
import { ChartSkeleton } from "./ChartSkeleton";
import { buildSeries, DateUnit, formatSpendValue, toChartRows } from "./spendChartRows";

export type Dataset = {
  label: string;
  data: SpendChartData[];
  id: string;
  /** A fixed colour for this series. Without one, series take palette slots in order. */
  color?: ColorProp;
  /** Keep this series after the others, whatever its size, e.g. a catch-all group. */
  sortLast?: boolean;
};

type ChartProps = {
  data: Dataset[];
  dateUnit: DateUnit;
  isLoading?: boolean;
  /** What the series values are, so the axis and tooltip format them right. @default "totalValue" */
  measure: SpendMeasure;
  /** The shape to draw. */
  chartType: SpendChartType;
};

type SpendChartData = {
  date: string;
  total: number;
};

const CHART_HEIGHT = 470;
// Ten public sector buyer names at full length wrap to three or four rows and take
// most of the plot height with them.
const LEGEND_LABEL_WIDTH = 180;

/** One stack, so a shape that stacks puts every series in it. */
const STACK_ID = "spend";

/**
 * The mark for one series, in the chosen shape.
 *
 * A function rather than a component: recharts reads its children to work out the axes
 * and the stacks, and only recognises its own `Area` / `Bar` / `Line` elements there. A
 * wrapper component would put something it doesn't know in that list, and the series
 * would never be plotted.
 */
function seriesMark(
  chartType: SpendChartType,
  key: string,
  seriesProps: { hide: boolean },
): ReactElement {
  const colour = `var(--color-${key})`;
  // The same colour mixed down into the page, for a fill that sits under a
  // full-strength line or outline without competing with it.
  const softColour = `var(--color-${key}-soft)`;

  switch (chartType) {
    case "stackedBar":
      // Bars for discrete periods: a year or a quarter is a bucket, not a point on a
      // continuous line, and an area between two of them implies a path the spend
      // didn't take.
      return (
        <Bar
          key={key}
          dataKey={key}
          stackId={STACK_ID}
          // Outlined like the area's bands: ten saturated blocks stacked on each other
          // is a lot of ink for a chart you're meant to read the shape of, and the
          // outline carries the identity the fill no longer has to. It also gives
          // touching segments an edge, so the stack reads as parts rather than one
          // column. 1px, not the area's 2: a bar is outlined on all four sides, and two
          // touching segments put their strokes side by side.
          fill={softColour}
          stroke={colour}
          strokeWidth={1}
          isAnimationActive={false}
          {...seriesProps}
        />
      );

    case "stackedArea":
      // One stack, so the bands sit on each other and the top edge reads as the total.
      // Series are ordered biggest first, which puts the largest band along the
      // baseline where its shape is easiest to follow.
      return (
        <Area
          key={key}
          dataKey={key}
          stackId={STACK_ID}
          type="linear"
          // The line carries the series' identity and the band reads as its territory.
          stroke={colour}
          strokeWidth={2}
          fill={softColour}
          // recharts fills an Area at 0.6 by default, which would composite the
          // already-mixed colour into the page a second time and let the gridlines
          // through. The mix is the whole tint.
          fillOpacity={1}
          isAnimationActive={false}
          activeDot={{ r: 4 }}
          // A hidden series drops out of the stack entirely, so isolating one from the
          // legend rescales the chart around it.
          {...seriesProps}
        />
      );

    case "line":
      return (
        <Line
          key={key}
          dataKey={key}
          type="linear"
          stroke={colour}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
          activeDot={{ r: 5 }}
          {...seriesProps}
        />
      );
  }
}

export default function SpendChart({ data, dateUnit, isLoading, measure, chartType }: ChartProps) {
  const isCount = measure === "transactionCount";
  const formatValue = useMemo(
    () => (isCount ? (value: number) => numberFormatLong(value) : formatSpendValue),
    [isCount],
  );
  const series = useMemo(() => buildSeries(data), [data]);
  const rows = useMemo(() => toChartRows(series, dateUnit), [series, dateUnit]);
  const seriesKeys = useMemo(() => series.map(({ key }) => key), [series]);
  const { legendProps, seriesProps } = useLegendToggle(seriesKeys);

  const config = useMemo<ChartConfig>(
    () =>
      Object.fromEntries(
        series.map(({ key, dataset }) => [
          key,
          {
            // The legend renders nothing without a label. Unlike the tooltip, it has
            // no fallback to the series name.
            label: dataset.label,
            color: dataset.color,
            tooltipFormatter: (value: number) => formatValue(value),
          },
        ]),
      ),
    [series, formatValue],
  );

  if (isLoading) {
    return (
      <ChartFrame>
        <ChartSkeleton height={CHART_HEIGHT} label="Loading spend chart" />
      </ChartFrame>
    );
  }

  if (rows.length === 0) {
    return (
      <ChartFrame>
        <EmptyState
          type="noResults"
          heading="No spend in this period"
          description="Try widening the date range, or removing a filter."
        />
      </ChartFrame>
    );
  }

  return (
    <StyledChartContainer config={config}>
      <ComposedChart accessibilityLayer data={rows} margin={{ left: 12, right: 12 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="period" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} />
        <YAxis
          width={72}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value: number) => formatShortAmount(value)}
          // "auto" rather than the data max: pinning the top to the exact maximum
          // makes the last gridline land on a raw value, so the axis reads
          // 0 / 55B / 110B / 214.7B instead of even steps.
          domain={[0, "auto"]}
          label={{
            value: isCount ? "Transactions" : "Value GBP",
            angle: -90,
            position: "insideLeft",
          }}
        />
        <ChartTooltip
          itemSorter={(item) => -Number(item.value ?? 0)}
          content={<SpendTooltipContent />}
        />
        <ChartLegend
          verticalAlign="bottom"
          content={<ChartLegendContent {...legendProps} maxLabelWidth={LEGEND_LABEL_WIDTH} />}
        />
        {seriesKeys.map((key) => seriesMark(chartType, key, seriesProps(key)))}
      </ComposedChart>
    </StyledChartContainer>
  );
}

const ChartFrame = styled.div({
  height: CHART_HEIGHT,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledChartContainer = styled(ChartContainer)({
  height: CHART_HEIGHT,
  width: "100%",
});

// Ten rows at their natural width make a tooltip that covers the plot it describes.
// The rows inherit the gap, so the one override loosens the whole tooltip.
const SpendTooltipContent = styled(ChartTooltipContent)({
  maxWidth: 280,
  padding: "0.75rem",
  gap: "0.75rem",
});
